import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Database, 
  Users, 
  FileText, 
  CheckCircle, 
  AlertCircle, 
  Loader,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { 
  runFullMigration,
  runUserMigration,
  getMigrationStatus,
  createSampleData,
  verifyMigration
} from '../../utils/migrationRunner';

const DataMigration = () => {
  const [migrationStatus, setMigrationStatus] = useState('idle'); // idle, running, completed, error
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [migrationResults, setMigrationResults] = useState({});
  const [error, setError] = useState(null);

  const migrationSteps = [
    { id: 'initialize', name: 'Initialize Subcollections', description: 'Set up user subcollection structure' },
    { id: 'testResults', name: 'Migrate Test Results', description: 'Move test results to user subcollections' },
    { id: 'notes', name: 'Migrate Notes', description: 'Move user notes to subcollections' },
    { id: 'doubts', name: 'Migrate Doubts', description: 'Move user doubts to subcollections' },
    { id: 'progress', name: 'Migrate Progress', description: 'Move user progress data to subcollections' },
    { id: 'activities', name: 'Migrate Activities', description: 'Move user activities to subcollections' },
    { id: 'courses', name: 'Migrate Course Data', description: 'Move course enrollments to subcollections' },
    { id: 'complete', name: 'Migration Complete', description: 'Verify and finalize migration' }
  ];

  const startMigration = async () => {
    setMigrationStatus('running');
    setProgress(0);
    setError(null);
    setMigrationResults({});

    try {
      // Step 1: Initialize subcollections
      setCurrentStep('initialize');
      setProgress(10);
      
      // Step 2: Run full migration
      setCurrentStep('testResults');
      setProgress(20);
      
      const migrationResult = await runFullMigration();
      
      if (migrationResult.success) {
        const status = migrationResult.status;
        setMigrationResults({
          testResults: status.migratedTestResults,
          notes: status.migratedNotes,
          doubts: status.migratedDoubts,
          progress: status.migratedProgress,
          activities: status.migratedActivities,
          courses: status.migratedCourses
        });
        
        // Step 3: Complete migration
        setCurrentStep('complete');
        setProgress(100);
        setMigrationStatus('completed');
      } else {
        throw new Error(migrationResult.error || 'Migration failed');
      }
    } catch (err) {
      setError(err.message);
      setMigrationStatus('error');
    }
  };

  const resetMigration = () => {
    setMigrationStatus('idle');
    setProgress(0);
    setCurrentStep('');
    setMigrationResults({});
    setError(null);
  };

  const getCurrentStepInfo = () => {
    return migrationSteps.find(step => step.id === currentStep) || migrationSteps[0];
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="p-3 bg-blue-100 rounded-lg mr-4">
              <Database className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Data Migration</h1>
              <p className="text-gray-600 mt-1">Migrate from collections + arrays to subcollections structure</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-2">Current Structure</h3>
              <p className="text-sm text-blue-700">Collections with arrays</p>
              <div className="text-xs text-blue-600 mt-2">
                users/{'{userId}'} → {`{testResults: [], notes: [], ...}`}
              </div>
            </div>
            
            <div className="flex items-center justify-center">
              <ArrowRight className="h-6 w-6 text-gray-400" />
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="font-semibold text-green-900 mb-2">New Structure</h3>
              <p className="text-sm text-green-700">Subcollections</p>
              <div className="text-xs text-green-600 mt-2">
                users/{'{userId}'}/testResults/{'{testId}'}
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Migration Notice</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  This migration will move your data from the old structure to the new subcollections structure. 
                  The process is safe and will preserve all your data. Make sure to backup your data before proceeding.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Migration Controls */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Migration Controls</h2>
            {migrationStatus === 'idle' && (
              <div className="flex space-x-4">
                <button
                  onClick={startMigration}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  <Database className="h-5 w-5 mr-2" />
                  Start Full Migration
                </button>
                <button
                  onClick={async () => {
                    setMigrationStatus('running');
                    setCurrentStep('initialize');
                    try {
                      const result = await runUserMigration('test-user-123');
                      if (result.success) {
                        setMigrationStatus('completed');
                        setMigrationResults({ testResults: 1, notes: 1, doubts: 1, progress: 1, activities: 1, courses: 1 });
                      } else {
                        setError(result.error);
                        setMigrationStatus('error');
                      }
                    } catch (err) {
                      setError(err.message);
                      setMigrationStatus('error');
                    }
                  }}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
                >
                  <Database className="h-5 w-5 mr-2" />
                  Test Migration
                </button>
              </div>
            )}
            {migrationStatus === 'completed' && (
              <button
                onClick={resetMigration}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center"
              >
                <RefreshCw className="h-5 w-5 mr-2" />
                Reset Migration
              </button>
            )}
          </div>

          {/* Progress Bar */}
          {migrationStatus === 'running' && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Migration Progress</span>
                <span className="text-sm text-gray-500">{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <motion.div
                  className="bg-blue-600 h-3 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
          )}

          {/* Current Step */}
          {migrationStatus === 'running' && currentStep && (
            <div className="mb-6">
              <div className="flex items-center mb-2">
                <Loader className="h-5 w-5 text-blue-600 mr-2 animate-spin" />
                <span className="text-sm font-medium text-gray-700">Current Step:</span>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900">{getCurrentStepInfo().name}</h3>
                <p className="text-sm text-blue-700">{getCurrentStepInfo().description}</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {migrationStatus === 'error' && error && (
            <div className="mb-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-red-800">Migration Error</h3>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Success State */}
          {migrationStatus === 'completed' && (
            <div className="mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-green-800">Migration Completed Successfully!</h3>
                    <p className="text-sm text-green-700 mt-1">
                      Your data has been successfully migrated to the new subcollections structure.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Migration Steps */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Migration Steps</h2>
          <div className="space-y-4">
            {migrationSteps.map((step, index) => {
              const isCurrent = currentStep === step.id;
              const isCompleted = migrationStatus === 'completed' || 
                (migrationStatus === 'running' && index < migrationSteps.findIndex(s => s.id === currentStep));
              const isPending = !isCurrent && !isCompleted;

              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-center p-4 rounded-lg border ${
                    isCurrent ? 'bg-blue-50 border-blue-200' :
                    isCompleted ? 'bg-green-50 border-green-200' :
                    'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className={`p-2 rounded-lg mr-4 ${
                    isCurrent ? 'bg-blue-100' :
                    isCompleted ? 'bg-green-100' :
                    'bg-gray-100'
                  }`}>
                    {isCurrent ? (
                      <Loader className="h-5 w-5 text-blue-600 animate-spin" />
                    ) : isCompleted ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <div className="h-5 w-5 text-gray-400">{index + 1}</div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-medium ${
                      isCurrent ? 'text-blue-900' :
                      isCompleted ? 'text-green-900' :
                      'text-gray-700'
                    }`}>
                      {step.name}
                    </h3>
                    <p className={`text-sm ${
                      isCurrent ? 'text-blue-700' :
                      isCompleted ? 'text-green-700' :
                      'text-gray-500'
                    }`}>
                      {step.description}
                    </p>
                  </div>
                  {migrationResults[step.id] && (
                    <div className="text-sm text-gray-500">
                      {migrationResults[step.id]} items
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Migration Benefits */}
        <div className="bg-white rounded-lg shadow-lg p-8 mt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Benefits of Subcollections</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <div className="p-2 bg-green-100 rounded-lg mr-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-1">Better Performance</h3>
                <p className="text-sm text-gray-600">Only load the data you need, reducing bandwidth and improving load times</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="p-2 bg-green-100 rounded-lg mr-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-1">Scalability</h3>
                <p className="text-sm text-gray-600">No document size limits, handle unlimited data per user</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="p-2 bg-green-100 rounded-lg mr-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-1">Real-time Updates</h3>
                <p className="text-sm text-gray-600">Listen to specific subcollections for real-time data changes</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="p-2 bg-green-100 rounded-lg mr-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-1">Better Security</h3>
                <p className="text-sm text-gray-600">Easier to secure user data with subcollection-level permissions</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataMigration;
